import requests from '@/utils/requests';

/**
 * Helper function to handle driver authentication errors
 * Logs out driver and redirects to login on 401 errors
 */
function handleAuthError(error, dispatch) {
  if (error.response?.status === 401) {
    console.log('Driver session expired, redirecting to login...');
    
    // Logout driver
    dispatch('logout');
    window.location.href = '/driver/login';
    
    return true;
  }
  
  return false; // Not an auth error
}

const state = {
  driver: null,
  trips: [],
  activeTrip: null,
  loading: false,
  error: null
};

const getters = {
  isAuthenticated: (state) => !!state.driver,
  activeTrips: (state) => state.trips,
  currentTrip: (state) => state.activeTrip,
  isLoading: (state) => state.loading,
  getError: (state) => state.error,
  driverToken: () => localStorage.getItem('driver_token')
};

const actions = {
  /**
   * Login driver
   */
  async login({ commit }, { email, phone }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const response = await requests('POST', 'api/common/driver/login', null, { email, phone });
      
      if (response.data.success && response.data.token) {
        // Store token and driver info
        localStorage.setItem('driver_token', response.data.token);
        localStorage.setItem('driver_info', JSON.stringify(response.data.driver));
        commit('SET_DRIVER', response.data.driver);
        return { success: true };
      }
      throw new Error('Login failed');
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Login failed';
      commit('SET_ERROR', errorMsg);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Logout driver
   */
  logout({ commit }) {
    localStorage.removeItem('driver_token');
    localStorage.removeItem('driver_info');
    commit('CLEAR_DRIVER');
  },

  /**
   * Load driver info from localStorage
   */
  loadDriver({ commit }) {
    try {
      const driverInfo = localStorage.getItem('driver_info');
      if (driverInfo) {
        commit('SET_DRIVER', JSON.parse(driverInfo));
      }
    } catch (error) {
      console.error('Error loading driver info:', error);
    }
  },

  /**
   * Fetch active trips
   */
  async fetchActiveTrips({ commit, dispatch, getters }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const token = getters.driverToken;
      const response = await requests('GET', 'api/driver/trips', token);
      commit('SET_TRIPS', response.data.data || []);
    } catch (error) {
      // Handle authentication errors (token expired)
      if (handleAuthError(error, dispatch)) {
        throw error; // Re-throw to be caught by component
      }
      
      const errorMsg = error.response?.data?.error || 'Failed to load trips';
      commit('SET_ERROR', errorMsg);
      console.error('Error fetching active trips:', error);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Fetch trip details
   */
  async fetchTripDetails({ commit, dispatch, getters }, tripId) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const token = getters.driverToken;
      const response = await requests('GET', `api/driver/trips/${tripId}`, token);
      commit('SET_ACTIVE_TRIP', response.data.data);
      return response.data.data;
    } catch (error) {
      // Handle authentication errors (token expired)
      if (handleAuthError(error, dispatch)) {
        throw error;
      }
      
      const errorMsg = error.response?.data?.error || 'Failed to load trip details';
      commit('SET_ERROR', errorMsg);
      console.error('Error fetching trip details:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Update trip status
   */
  async updateTripStatus({ commit, dispatch, getters }, { tripId, status, actual_duration }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const token = getters.driverToken;
      const payload = { status };
      
      // Include actual_duration if provided (for COMPLETED status)
      // Note: actual_distance is no longer sent from frontend
      // Backend will use estimated_distance (from Google Maps) instead
      if (actual_duration !== undefined) {
        payload.actual_duration = actual_duration;
      }
      
      const response = await requests('PUT', `api/driver/trips/${tripId}/status`, token, payload);
      
      // Refresh active trips after status update
      await dispatch('fetchActiveTrips');
      
      return response.data.data;
    } catch (error) {
      // Handle authentication errors (token expired)
      if (handleAuthError(error, dispatch)) {
        throw error;
      }
      
      const errorMsg = error.response?.data?.error || 'Failed to update trip status';
      commit('SET_ERROR', errorMsg);
      console.error('Error updating trip status:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Start delivery (update calendar event to ON_WAY_TO_WAREHOUSE)
   */
  async startDelivery({ commit, dispatch, getters }, { tripId, estimated_distance, estimated_duration }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const token = getters.driverToken;
      const payload = {};
      
      // Include distance and duration if provided
      if (estimated_distance !== null && estimated_distance !== undefined) {
        payload.estimated_distance = estimated_distance;
      }
      if (estimated_duration !== null && estimated_duration !== undefined) {
        payload.estimated_duration = estimated_duration;
      }
      
      const response = await requests('POST', `api/driver/trips/${tripId}/start-delivery`, token, payload);
      
      // Refresh active trips after starting delivery
      await dispatch('fetchActiveTrips');
      
      return response.data;
    } catch (error) {
      // Handle authentication errors (token expired)
      if (handleAuthError(error, dispatch)) {
        throw error;
      }
      
      const errorMsg = error.response?.data?.error || 'Failed to start delivery';
      commit('SET_ERROR', errorMsg);
      console.error('Error starting delivery:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Update driver location
   */
  async updateLocation({ dispatch, getters }, { lat, lng }) {
    try {
      const token = getters.driverToken;
      await requests('PUT', 'api/driver/location', token, { lat, lng });
    } catch (error) {
      // Handle authentication errors (token expired)
      if (handleAuthError(error, dispatch)) {
        return; // Stop processing if auth error
      }
      
      console.error('Error updating location:', error);
      // Don't throw - location updates should fail silently for other errors
    }
  },

  /**
   * Set active trip
   */
  setActiveTrip({ commit }, trip) {
    commit('SET_ACTIVE_TRIP', trip);
  }
};

const mutations = {
  SET_DRIVER(state, driver) {
    state.driver = driver;
  },

  CLEAR_DRIVER(state) {
    state.driver = null;
    state.trips = [];
    state.activeTrip = null;
  },

  SET_TRIPS(state, trips) {
    state.trips = trips;
  },

  SET_ACTIVE_TRIP(state, trip) {
    state.activeTrip = trip;
  },

  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_ERROR(state, error) {
    state.error = error;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};

